import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {
  Tooltip,
  TooltipPlacement
} from '@vio/atlas/src/components/designSystem/Tooltip'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {formatAbbreviatedPriceValue} from '@vio/core/src/price/business/formatPrice'

import {priceTrendMessages} from '../../utils/messages'
import {
  BAR_HEIGHT,
  SEGMENT_GAP_PX,
  SEGMENT_RADIUS,
  TOOLTIP_ROW_HEIGHT
} from './constants'
import {
  getBarBounds,
  getIndicatorPercent,
  isValidPriceRangeData,
  PriceRangeBarData
} from './utils'

/**
 * Price Insights Price Range Bar (Dealscore).
 *
 * Mirrors the Figma Dealscore component: a flex column with
 * `gap: layout.spacing.s500` (16 px) between every row — tooltip, bar,
 * labels row, boundary prices row. The bar itself is three equally-sized
 * rounded-pill segments (Low / Fair / High).
 *
 * Indicator snap-to-centre rule (boundaries inclusive on the outer bands):
 * - hotelPrice ≤ p35 → Low centre
 * - p35 < hotelPrice < p65 → Fair centre
 * - hotelPrice ≥ p65 → High centre
 *
 * When the hotel price falls outside the Fair band it anchors the
 * opposing outer bound so the leftmost / rightmost edge labels stay in
 * sync with reality.
 */
export interface PriceRangeBarProps extends PriceRangeBarData {
  /** ISO currency code used to format prices. */
  currency: string
  /** Optional node rendered inside the tooltip before the hotel price (e.g. a Vio logo). */
  tooltipIconSlot?: ReactNode
  /** Show the "? Price compared to similar hotels nearby." help row below the prices (default true). */
  showHelpHint?: boolean
  /** Test identifier forwarded as `data-id` on the root element. */
  dataTestId?: string
}

// Segment colours mapped to Atlas brand-themed primitives per the Figma spec
// so the bar recolours correctly per brand (Vio, Ovago, …) instead of
// drifting from the design system:
//   low  → success.c700 (primary "good" green)
//   fair → accent.c600  (mid-weight warm accent for the neutral/fair band)
//   high → neutral.c400 (muted grey for the "above market" band)
export const SEGMENT_COLORS = {
  low: cssTheme.colors.success.c700,
  fair: cssTheme.colors.accent.c600,
  high: cssTheme.colors.neutral.c400
} as const

// Per Figma: 10px rhythm between every row (tooltip → bar, bar → labels,
// labels → boundary prices, boundary prices → help). TooltipAnchor anchors the
// tail tip to the bottom of TooltipRow, so this gap is also the arrow-to-bar
// distance.
const Root = styled.div`
  width: 100%;
`

// Can't use Atlas token here as Figma is not taking into consideration the Tooltip Anchor in the spacing
const TooltipRow = styled.div`
  position: relative;
  width: 100%;
  height: ${TOOLTIP_ROW_HEIGHT}px;
  margin-bottom: calc(${cssTheme.layout.spacing.s400} - 1px);
`

const TooltipAnchor = styled.div<{percent: number}>`
  position: absolute;
  bottom: 0;
  left: ${({percent}) => percent}%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`

const TooltipBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  background: ${cssTheme.colors.neutral.c000};
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  padding: ${cssTheme.layout.spacing.s200} ${cssTheme.layout.spacing.s300};
  white-space: nowrap;
`

// Triangle drawn with borders. left/right = half the visual width, so 7px
// each gives the Figma-spec 14px wide tail; border-top height = 6px tail
// height. margin-top: -1px hides a hairline gap with the bubble bottom.
const TooltipTail = styled.div`
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 6px solid ${cssTheme.colors.neutral.c000};
  margin-top: -1px;
`

const BarTrack = styled.div`
  display: flex;
  gap: ${SEGMENT_GAP_PX}px;
  width: 100%;
  height: ${BAR_HEIGHT}px;
`

const Segment = styled.div<{color: string; position: 'low' | 'fair' | 'high'}>`
  flex: 1 1 0;
  background-color: ${({color}) => color};
  border-top-left-radius: ${({position}) => SEGMENT_RADIUS[position].tl};
  border-top-right-radius: ${({position}) => SEGMENT_RADIUS[position].tr};
  border-bottom-right-radius: ${({position}) => SEGMENT_RADIUS[position].br};
  border-bottom-left-radius: ${({position}) => SEGMENT_RADIUS[position].bl};
`

// Three labels evenly spaced — grid columns mirror the three bar segments,
// each label centred in its column. Intrinsic text height sets the row
// height so no explicit `height` is needed.
const LabelsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  margin-top: ${cssTheme.layout.spacing.s250};
`

const Label = styled.div`
  justify-self: center;
  white-space: nowrap;
`

const PricesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  margin-top: ${cssTheme.layout.spacing.s200};
`

const PriceCell = styled.div<{align: 'start' | 'end'; column?: number}>`
  grid-row: 1;
  ${({column}) => (column ? `grid-column: ${column};` : '')}
  justify-self: ${({align}) => align};
  white-space: nowrap;
`

const HelpRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  margin-top: ${cssTheme.layout.spacing.s500};
`

const HelpTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${cssTheme.colors.content.neutral.secondary};
`

const HelpTooltip = styled(Tooltip)`
  max-width: 100%;
`

export const PriceRangeBar: React.FC<PriceRangeBarProps> = ({
  p10,
  p35,
  p65,
  p90,
  hotelPrice,
  currency,
  tooltipIconSlot,
  showHelpHint = true,
  dataTestId = 'price-range-bar'
}) => {
  const {formatMessage, formatNumber, locale} = useIntl()
  // Ref (not state) — the Atlas Tooltip only needs the Element when `isOpen`
  // flips to true, and `isOpen` already drives a re-render, so reading
  // `.current` at render time is safe and avoids an extra mount render.
  const helpRowRef = useRef<HTMLDivElement | null>(null)
  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false)

  // Tap-outside dismissal: while the help tooltip is open, listen for
  // pointerdown events anywhere in the document and close if the target is
  // outside the help-row trigger. The Atlas Tooltip renders in a portal so
  // a click on its body lands outside `helpRowRef.current` — that closes
  // the tooltip too, which matches typical "help bubble" UX.
  useEffect(() => {
    if (!helpTooltipOpen) return
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (target && !helpRowRef.current?.contains(target)) {
        setHelpTooltipOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [helpTooltipOpen])

  const data: PriceRangeBarData = {p10, p35, p65, p90, hotelPrice}

  const derived = useMemo(() => {
    if (!isValidPriceRangeData(data)) return null
    const bounds = getBarBounds(data)
    return {
      bounds,
      indicatorPct: getIndicatorPercent(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p10, p35, p65, p90, hotelPrice])

  if (!derived) return null

  // Tooltip shows the exact offer price for trust (no abbreviation).
  const formatPrice = (value: number) =>
    formatNumber(value, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    })

  // Boundary labels use the chart's abbreviation method so long currencies
  // (¥, ₩, IDR, VND…) collapse to e.g. ¥9k / ¥25k+ instead of overflowing.
  const formatBoundaryPrice = (value: number) =>
    formatAbbreviatedPriceValue({
      locale,
      value,
      currency,
      useShortCurrency: false,
      roundingStrategy: 'round'
    })

  // The upper bound shares column 3 with p65 and on narrow viewports the
  // verbose form (PLN 1.984, BRL 1.700…) crowds p65. formatAbbreviatedPriceValue
  // only switches to "k" at ≥ 10,000, so for 1k–10k mid-range currencies we
  // force the k-abbreviation locally: format the currency around a "0"
  // placeholder, then substitute the compact number + "k".
  const formatCompactBoundaryPrice = (value: number) => {
    if (value < 1000 || value >= 10000) return formatBoundaryPrice(value)
    const inK = value / 1000
    const rounded = Math.round(inK * 10) / 10
    const numberPart = formatNumber(rounded, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1
    })
    const scaffold = formatNumber(0, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    })
    return scaffold.replace(/\d+/, `${numberPart}k`)
  }

  const {bounds, indicatorPct} = derived

  const lowLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentLow
  )
  const fairLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentFair
  )
  const highLabel = formatMessage(
    priceTrendMessages.priceInsightsDealscoreSegmentHigh
  )

  return (
    <Box
      role="meter"
      aria-valuemin={bounds.leftBound}
      aria-valuemax={bounds.rightBound}
      aria-valuenow={hotelPrice}
      data-id={dataTestId}
    >
      <Root>
        <TooltipRow>
          <TooltipAnchor
            percent={indicatorPct}
            data-id={`${dataTestId}-indicator`}
          >
            <TooltipBubble>
              {tooltipIconSlot}
              <Text variant="labelM" colorPath="content.neutral.primary">
                {formatPrice(hotelPrice)}
              </Text>
            </TooltipBubble>
            <TooltipTail />
          </TooltipAnchor>
        </TooltipRow>
        <BarTrack>
          <Segment position="low" color={SEGMENT_COLORS.low} />
          <Segment position="fair" color={SEGMENT_COLORS.fair} />
          <Segment position="high" color={SEGMENT_COLORS.high} />
        </BarTrack>
        <LabelsRow>
          <Label>
            <Text variant="titleXS" colorPath="content.neutral.primary">
              {lowLabel}
            </Text>
          </Label>
          <Label>
            <Text variant="titleXS" colorPath="content.neutral.primary">
              {fairLabel}
            </Text>
          </Label>
          <Label>
            <Text variant="titleXS" colorPath="content.neutral.primary">
              {highLabel}
            </Text>
          </Label>
        </LabelsRow>
        <PricesRow>
          <PriceCell align="start" column={1}>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {formatBoundaryPrice(bounds.leftBound)}
            </Text>
          </PriceCell>
          <PriceCell align="start" column={2}>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {formatBoundaryPrice(p35)}
            </Text>
          </PriceCell>
          <PriceCell align="start" column={3}>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {formatBoundaryPrice(p65)}
            </Text>
          </PriceCell>
          <PriceCell align="end" column={3}>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {`${formatCompactBoundaryPrice(bounds.rightBound)}+`}
            </Text>
          </PriceCell>
        </PricesRow>
        {showHelpHint && (
          <HelpRow ref={helpRowRef}>
            {/* aria-label intentionally carries the tooltip content, not the
                visible help-hint text: it gives the icon-only button a
                meaningful accessible name describing what opens on click. */}
            <HelpTrigger
              type="button"
              aria-label={formatMessage(
                priceTrendMessages.priceInsightsDealscoreSimilarHotelsTooltip
              )}
              data-id={`${dataTestId}-help-trigger`}
              onClick={() => setHelpTooltipOpen(v => !v)}
            >
              <Icon
                name="HelpCircle"
                size="sm"
                colorPath="content.neutral.primary"
              />
            </HelpTrigger>
            <Text variant="bodyXS" colorPath="content.neutral.primary">
              {formatMessage(
                priceTrendMessages.priceInsightsDealscoreFooterLabel
              )}
            </Text>
            <HelpTooltip
              placement={TooltipPlacement.Top}
              content={formatMessage(
                priceTrendMessages.priceInsightsDealscoreSimilarHotelsTooltip
              )}
              anchor={helpRowRef.current}
              isOpen={helpTooltipOpen}
              onClose={() => setHelpTooltipOpen(false)}
            />
          </HelpRow>
        )}
      </Root>
    </Box>
  )
}
